// app/javascript/controllers/analytics_realtime_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    pollInterval: { type: Number, default: 5000 }  // 5s for near-instant updates
  }

  connect() {
    console.log('Analytics Realtime: Controller connected')

    // Initialize previous state for delta tracking
    this.previousData = {
      pages: {},
      total_clicks: 0,
      total_visitors: 0
    }

    const userRole = document.body.dataset.userRole

    if (!userRole || !['admin', 'super_admin'].includes(userRole)) {
      console.log('Analytics: User not authorized, skipping polling')
      return
    }

    console.log(`Analytics: Starting polling for ${userRole}`)
    this.startPolling()
  }

  startPolling() {
    this.fetchRealtimeData()
    this.fetchActivePagesData()

    this.pollTimer = setInterval(() => {
      this.fetchRealtimeData()
      this.fetchActivePagesData()
    }, this.pollIntervalValue)

    console.log(`Analytics: Polling started (every ${this.pollIntervalValue / 1000}s)`)
  }

  async fetchRealtimeData() {
    try {
      const response = await fetch('/admin/analytics/realtime', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (response.status === 401) {
        console.warn('Analytics: Unauthorized, session expired')
        this.stopPolling()
        return
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      this.updateRealtimeMetrics(data)

    } catch (error) {
      console.error('Analytics realtime fetch error:', error.message)
    }
  }

  async fetchActivePagesData() {
    try {
      const response = await fetch('/admin/analytics/active_pages', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (response.status === 401) {
        console.warn('Analytics: Unauthorized')
        this.stopPolling()
        return
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      this.updateActivePagesWidget(data)

    } catch (error) {
      console.error('Analytics active_pages fetch error:', error.message)
    }
  }

  updateRealtimeMetrics(data) {
    const activeVisitorsEl = document.querySelector('[data-analytics-active-visitors]')
    if (activeVisitorsEl && data.active_visitors !== undefined) {
      activeVisitorsEl.textContent = data.active_visitors
      this.animateCount(activeVisitorsEl)
    }
  }

  updateActivePagesWidget(data) {
    const container = document.getElementById('live_activity_list')
    const totalVisitorsEl = document.getElementById('total_active_visitors')
    const totalClicksEl = document.getElementById('total_clicks_live')

    // Update totals with delta indicators
    if (totalVisitorsEl) {
      const delta = data.total_active - this.previousData.total_visitors
      totalVisitorsEl.textContent = data.total_active || 0
      this.animateCountWithDelta(totalVisitorsEl, delta)
    }

    if (totalClicksEl) {
      const delta = (data.total_clicks || 0) - this.previousData.total_clicks
      totalClicksEl.textContent = data.total_clicks || 0
      this.animateCountWithDelta(totalClicksEl, delta)
    }

    // Update pages list
    if (container) {
      if (!data.active_pages || data.active_pages.length === 0) {
        container.innerHTML = this.emptyStateHTML()
      } else {
        container.innerHTML = data.active_pages.map(page => {
          const previousPage = this.previousData.pages[page.path] || { clicks: 0, visitors: 0 }
          const clickDelta = page.clicks - previousPage.clicks
          const visitorDelta = page.visitors - previousPage.visitors

          return this.pageItemHTML(page, clickDelta, visitorDelta)
        }).join('')
      }
    }

    // Store current state for next comparison
    this.previousData = {
      pages: data.active_pages.reduce((acc, page) => {
        acc[page.path] = { clicks: page.clicks, visitors: page.visitors }
        return acc
      }, {}),
      total_clicks: data.total_clicks || 0,
      total_visitors: data.total_active || 0
    }

    console.log('Analytics: Active pages updated', data)
  }

  pageItemHTML(page, clickDelta, visitorDelta) {
    const pathLabel = this.formatPagePath(page.path)
    const visitorIcon = page.visitors === 1 ? '👤' : '👥'
    const clickIcon = '👆'
    const isNew = page.path === '/sentinel' || page.path?.includes('sentinel')

    return `
      <div class="flex items-center justify-between py-4 px-6 hover:bg-gray-50 transition-all duration-200 border-b border-gray-100 last:border-0">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-green-500 text-xs animate-pulse">●</span>
            <span class="font-semibold text-gray-900 truncate text-sm">${pathLabel}</span>
            ${isNew ? '<span class="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-bold animate-pulse">🔥 Hot</span>' : ''}
          </div>
          <p class="text-xs text-gray-500 mt-1 truncate">${page.path}</p>
        </div>
        
        <div class="flex items-center gap-5 ml-4">
          <!-- Visitors -->
          <div class="flex items-center gap-2 text-sm">
            <span class="opacity-70">${visitorIcon}</span>
            <div class="flex flex-col items-end">
              <span class="font-bold text-blue-600 text-base">${page.visitors}</span>
              ${this.deltaHTML(visitorDelta, 'blue')}
            </div>
          </div>
          
          <!-- Clicks -->
          ${page.clicks > 0 ? `
            <div class="flex items-center gap-2 text-sm">
              <span class="opacity-70">${clickIcon}</span>
              <div class="flex flex-col items-end">
                <span class="font-bold text-purple-600 text-base">${page.clicks}</span>
                ${this.deltaHTML(clickDelta, 'purple')}
              </div>
            </div>
          ` : `
            <div class="flex items-center gap-2 text-sm opacity-40">
              <span>${clickIcon}</span>
              <span class="text-gray-400 text-base">0</span>
            </div>
          `}
        </div>
      </div>
    `
  }

  deltaHTML(delta, color) {
    if (delta === 0) return ''

    const sign = delta > 0 ? '+' : ''
    const colorClass = delta > 0 ? `text-${color}-500` : 'text-gray-400'
    const arrow = delta > 0 ? '↑' : '↓'

    return `
      <span class="text-xs font-semibold ${colorClass} animate-pulse-once">
        ${arrow}${sign}${delta}
      </span>
    `
  }

  emptyStateHTML() {
    return `
      <div class="text-center py-16 px-4">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
          <svg class="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
            <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"/>
          </svg>
        </div>
        <p class="text-sm font-semibold text-gray-700 mb-1">No active visitors right now</p>
        <p class="text-xs text-gray-500">Real-time activity will appear here</p>
      </div>
    `
  }

  formatPagePath(path) {
    if (!path || path === '/') return 'Home'
    if (path === '/sentinel' || path.includes('/sentinel')) return 'Sentinel Game'
    if (path.startsWith('/courses')) return 'Courses'
    if (path.startsWith('/products')) return 'Products'
    if (path.startsWith('/funnel')) return 'Funnel'
    if (path.startsWith('/dashboard')) return 'Dashboard'
    if (path.startsWith('/checkouts')) return 'Checkout'
    if (path.startsWith('/payments')) return 'Payment'

    const segments = path.split('/').filter(Boolean)
    const lastSegment = segments[segments.length - 1] || 'Unknown'
    return lastSegment.replace(/-/g, ' ').replace(/_/g, ' ')
      .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }

  animateCount(element) {
    element.classList.remove('animate-pulse-once')
    void element.offsetWidth
    element.classList.add('animate-pulse-once')
    setTimeout(() => element.classList.remove('animate-pulse-once'), 600)
  }

  animateCountWithDelta(element, delta) {
    this.animateCount(element)

    if (delta > 0) {
      element.classList.add('text-green-600')
      setTimeout(() => element.classList.remove('text-green-600'), 1000)
    }
  }

  disconnect() {
    this.stopPolling()
    console.log('Analytics: Controller disconnected')
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
